"use client";

import { Edit, Trash2 } from "lucide-react";
import { CategoryItemChild } from "../types";

interface ItemRowProps {
  item: CategoryItemChild;
  isSelected: boolean;
  titleField: string;
  descField: string;
  showPriority?: boolean;
  onClick: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

export const ItemRow = ({
  item,
  isSelected,
  titleField,
  descField,
  showPriority = true,
  onClick,
  onEdit,
  onDelete,
}: ItemRowProps) => {
  return (
    <div
      className={`relative border rounded-lg p-3 transition-all cursor-pointer group ${
        isSelected
          ? "bg-blue-50 border-blue-300 shadow-sm"
          : "bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm"
      }`}
      onClick={onClick}
    >
      <div className="flex items-start gap-3">
        {/* Item Icon */}
        <div className="flex-shrink-0 mt-0.5">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center ${
              isSelected ? "bg-blue-100" : "bg-gray-100"
            }`}
          >
            <span className="text-sm">📄</span>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <h5
                className={`font-semibold text-sm mb-1 ${
                  isSelected ? "text-blue-900" : "text-gray-900"
                }`}
              >
                {item[titleField] || "Untitled"}
              </h5>
              {item[descField] && (
                <p className="text-xs text-gray-600 line-clamp-2 mb-2">
                  {descField === "credits" ? `Credits: ${item[descField]}` : item[descField]}
                </p>
              )}
              <div className="flex items-center gap-2 mt-2">
                {showPriority && item.priority !== undefined && (
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                    Priority: {item.priority || 1}
                  </span>
                )}
                {item.status === false && (
                  <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded">
                    Inactive
                  </span>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
                className="p-1.5 hover:bg-blue-100 rounded text-blue-600 transition-colors"
                title="Edit Item"
              >
                <Edit className="w-4 h-4" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                className="p-1.5 hover:bg-red-100 rounded text-red-600 transition-colors"
                title="Delete Item"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

